import type { ReactNode } from 'react';
import styles from './Switch.module.css';

interface SwitchProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  /** Visible text beside the switch. */
  readonly label?: string;
  /** For a switch whose label is elsewhere on the row (ADMIN-17). */
  readonly ariaLabel?: string;
  /**
   * Waiting for an answer before it moves (ADMIN-17, Admin redesign 2026-11-02): the thumb sits half-way and `checked`
   * stays what is saved, so the switch never shows a state nobody has agreed to yet.
   */
  readonly isWaiting?: boolean;
  /** The id of whatever explains it, such as the question it is waiting on. */
  readonly describedBy?: string;
}

/** An on/off switch, built once for R1's Master settings (CLAUDE.md §5: our controls, never stock). */
export function Switch({ checked, onChange, disabled = false, label, ariaLabel, isWaiting = false, describedBy }: SwitchProps): ReactNode {
  return (
    <label className={styles.wrap} data-disabled={disabled} data-waiting={isWaiting || undefined}>
      <input
        type="checkbox"
        role="switch"
        className={styles.input}
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        aria-checked={checked}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.track} aria-hidden>
        <span className={styles.thumb} />
      </span>
      {label !== undefined && <span className={styles.label}>{label}</span>}
    </label>
  );
}
